import { useState, useEffect } from 'react';
import { Badge, Button, Modal } from '@safetyhub/ui';
import { getDocStatusBadgeTone, ReportStatus, DeleteRequestChannel } from '../../lib/reports';
import { reportPostSubmitFlow } from '../../pages/reports/postSubmitFlows';
import {
  isApprovedWithoutApproval,
  resolveReportStatusLabel,
  type ReportStatusLabelSource,
} from '../../lib/reportStatusLabel';
import { MODAL_LAYER } from '../modalLayers';

/**
 * ⚠️ **라벨 판정 재료는 상속으로 받는다** — 손으로 나열하지 않는다.
 *
 * 이 대화상자는 `resolveReportStatusLabel(report)`로 헤더와 **같은 라벨**을 파생하는데, 그
 * 함수가 보는 축이 늘 때 이 형상이 따라가지 않으면 **같은 화면이 같은 문서를 두 이름으로
 * 부른다**(2026-09-08 dev 실측: 헤더 「진행 중」 / 이 대화상자 「초안」 — 흐름 축을 안 받아서였다).
 * `ReportStatusLabelSource`를 상속하면 재료가 늘 때 호출부에서 tsc가 막아 준다.
 */
interface ReportInfo extends ReportStatusLabelSource {
  title: string;
  /** 정본(`ReportStatusLabelSource.status: string`)을 이 화면 어휘로 좁힌다. */
  status: ReportStatus;
  authorName: string;
  siteName?: string;
  /** 보고서 유형(호출부 형상 그대로 — 판정에는 쓰지 않는다, 축은 흐름 선언이다). */
  type?: string | null;
  /**
   * 결재 서명 행. **라벨과 경고 문구가 같은 데이터 축에서 파생**하도록 받는다.
   *
   * `APPROVED`인데 0행이면 결재 정책이 없는 양식의 완료본이라(서버
   * `hasApprovalLinePolicy=false` → 제출 즉시 확정, 서명 행 없음) 상태는 '결재완료'가 아니라
   * '완료'이고, "결재가 완료되어"·"결재자들의 서명 기록"이라 적는 것도 거짓 안내다.
   * `undefined`(응답이 필드를 안 실었다)는 0행과 **다르게** 다룬다
   * (`isApprovedWithoutApproval` — 모르면 정본 라벨 유지).
   */
  approvalSignatures?: unknown[] | null;
  /** 결재가 진행 중인지 (하나 이상의 결재자가 승인) */
  hasApprovalInProgress?: boolean;
  /**
   * 교육 세션 행 수 — 교육 흐름 문서 경고 레벨의 **활동 축**이다(0이면 교육을 시작한 적 없다).
   *
   * 종전엔 문서 상태 `SIGNING`이 「교육이 시작됐다」를 답했는데, 그 값이 활동 축의 사실이라
   * 문서 enum에서 사라졌다(2026-09-08 `tbm-status-model-two-axes`). 상세·목록 응답이 같은
   * 이름으로 이 수를 싣는다(상세는 세션 행 길이, 목록은 `_count`).
   *
   * 세션 «행»이 아니라 **스칼라**인 이유: 이 대화상자는 상세뿐 아니라 **목록에서도** 열리는데
   * 행은 `findOne`만 싣는다. 행을 축으로 잡으면 목록에서만 경고가 조용히 죽는다.
   * 등가성도 이쪽이 정확하다 — 옛 `SIGNING`이 뜻한 것은 「교육이 시작됐다」이지 「진행 중
   * 세션이 있다」가 아니었다(교육이 끝나도 상신 전까지 `SIGNING`이 유지됐다).
   *
   * ⚠️ `undefined`(응답이 안 실었다)는 0이 **아니다** — 모르는 것이다. 삭제는 되돌릴 수 없어
   * 「모르면 약하게」가 아니라 **「모르면 강하게」**로 판정한다. 새 surface가 이 값을 빠뜨리면
   * 경고가 조용히 죽는 대신 과하게 뜬다(그쪽이 싼 실패다).
   */
  educationSessionCount?: number;
}

interface ReportDeleteConfirmDialogProps {
  /** 다이얼로그 표시 여부 */
  isOpen: boolean;
  /** 닫기 핸들러 */
  onClose: () => void;
  /** 삭제 확인 핸들러 (즉시 삭제용) */
  onDelete: () => void;
  /** 보고서 정보 */
  report: ReportInfo | null;
  /** 삭제 진행 중 */
  isLoading?: boolean;
  // 양식 전용 경고 갈래·상태 어휘 커스텀 prop을 두지 않는다 — 교육 활동 경고는 흐름 선언 축이 담당한다.
  /** 최종 결재자 승인이 필요한지 (APPROVED + 결재 서명 있음) */
  requiresApproval?: boolean;
  /** 최종 결재자 이름 */
  finalApproverName?: string;
  /**
   * 최종 결재자가 받을 수 있는 알림 채널.
   * 2개 모두 가용이면 체크박스로 선택(기본 둘 다 체크, 최소 1개 강제),
   * 1개만 가용이면 선택 없이 안내 + 자동.
   * 미지정이면 채널 선택 UI를 표시하지 않고 가용 전부로 발송(현행 호환).
   */
  availableChannels?: { sms: boolean; email: boolean };
  /** 삭제 승인 요청 핸들러 (channels: 가용 2개일 때만 선택값 전달, 그 외 undefined) */
  onRequestDelete?: (reason: string, channels?: DeleteRequestChannel[]) => void;
}

/**
 * 보고서 삭제 확인 다이얼로그
 * - 초안/반려 상태: 간단한 삭제 확인
 * - 결재 진행 중: 경고와 함께 삭제 확인
 * - 결재 완료: 강한 경고와 함께 삭제 확인
 */
export function ReportDeleteConfirmDialog({
  isOpen,
  onClose,
  onDelete,
  report,
  isLoading = false,
  requiresApproval = false,
  finalApproverName,
  availableChannels,
  onRequestDelete,
}: ReportDeleteConfirmDialogProps) {
  const [deleteReason, setDeleteReason] = useState('');
  const [sendSms, setSendSms] = useState(true);
  const [sendEmail, setSendEmail] = useState(true);

  // 가용 채널 분기
  const smsAvailable = !!availableChannels?.sms;
  const emailAvailable = !!availableChannels?.email;
  const availableCount = (smsAvailable ? 1 : 0) + (emailAvailable ? 1 : 0);
  // 2개 모두 가용일 때만 사용자 선택. 그 외(1개/0개/미지정)는 자동.
  const showChannelChoice = !!availableChannels && availableCount >= 2;

  useEffect(() => {
    if (!isOpen) {
      setDeleteReason('');
      setSendSms(true);
      setSendEmail(true);
    }
  }, [isOpen]);

  if (!isOpen || !report) return null;

  // 라벨은 한 축(서명 행)에서 파생한다 — 헤더와 같은 함수.
  const displayStatusLabel = resolveReportStatusLabel(report);
  const displayStatusTone = getDocStatusBadgeTone(report.status);

  /**
   * 활동 축이 **적용되는 문서인가** — 「이 문서가 TBM인가」가 아니라 **「이어질 흐름을 선언했는가」**다.
   *
   * 정체(레거시 `type` 컬럼·`templateKey`)로 「이건 TBM이다」를 유추하면 새 TBM 계열 템플릿이 하나
   * 생기는 순간 조용히 틀린다(레지스트리 doc의 「축은 type이지 templateKey가 아니다」와 같은 이유).
   * 그래서 정체를 묻지 않고 **선언**을 묻는다. 선언이 없는 평범한 보고서는 활동 축을 아예 타지
   * 않으므로, 아래 「모르면 강하게」 기본값이 그런 문서까지 강한 경고로 밀어 올리지 않는다.
   *
   * 판정 항은 선언 하나다 — 유형 항과의 합집합이 아니다.
   */
  const declaresActivityFlow = !!reportPostSubmitFlow(report);
  // 모르면(`undefined`) 시작한 것으로 본다 — 위 prop JSDoc의 「모르면 강하게」.
  const hasEducationActivity =
    declaresActivityFlow &&
    (report.educationSessionCount == null || report.educationSessionCount > 0);

  // 보고서 결재 상태 확인
  const isApprovalStartedOrCompleted =
    report.status === 'APPROVED' ||
    (report.status === 'PENDING_APPROVAL' && report.hasApprovalInProgress);

  const isApproved = report.status === 'APPROVED';

  /**
   * 결재 정책이 없는 양식의 완료본(APPROVED + 서명 0행).
   *
   * 되돌릴 수 없는 완료본이라 경고 강도(색·확인 문구)는 결재완료본과 같게 두되, **결재를 한 번도
   * 거치지 않았으므로** '결재완료'·'결재가 완료'·'결재자들의 서명 기록' 문구만 사실에 맞게 바꾼다.
   * 삭제 흐름 자체는 서버/`useReportPermissions` 양쪽 모두 제3자 결재 0건 → 즉시 삭제라 동일하다.
   */
  const isPolicylessCompletion = isApproved && isApprovedWithoutApproval(report);

  // 간단한 삭제: 초안/반려 + 교육 활동 없음.
  //
  // ⚠️ **활동 축**이 걸린다(2026-09-08). 종전엔 활동 축이 구 TBM 갈래 안에만 있어, 교육이 돌고
  // 참석자 서명이 남은 **COMPOSED TBM이 「이 보고서를 삭제하시겠습니까?」 단순 확인**으로
  // 지워졌다(dev 실측에서 적발). 선언이 없는 평범한 보고서는 `hasEducationActivity`가 항상
  // false라 종전 그대로다.
  const isSimpleDelete = !isApprovalStartedOrCompleted && !hasEducationActivity;

  /**
   * 경고의 **교육 갈래**(리뷰 W5, 2026-09-14) — 결재에는 안 올랐는데 교육이 돈 COMPOSED TBM.
   *
   * 종전엔 활동 축이 단순 삭제만 막고 경고 본문은 `!isApproved` 하나로 갈라, 이 문서에
   * 「결재 진행 중인 보고서 삭제 / 일부 결재자가 이미 승인하였습니다」가 떴다 — 둘 다 거짓이고,
   * 정작 잃는 것(참석자 서명·교육 기록)은 말하지 않았다.
   */
  const isEducationOnlyWarning = hasEducationActivity && !isApprovalStartedOrCompleted;

  /** 제목 — 본문 헤딩과 dialog의 접근 이름이 **같은 문자열**을 쓴다(두 벌로 갈라지지 않게). */
  const headline = isSimpleDelete
    ? '보고서 삭제'
    : isApproved
      ? `${displayStatusLabel}된 보고서 삭제`
      : isEducationOnlyWarning
        ? '교육이 진행 중인 보고서 삭제'
        : '결재 진행 중인 보고서 삭제';

  const footer = isSimpleDelete ? (
    <>
      <Button variant="secondary" onClick={onClose} disabled={isLoading}>
        취소
      </Button>
      <Button variant="danger" onClick={onDelete} disabled={isLoading}>
        {isLoading ? '삭제 중...' : '삭제'}
      </Button>
    </>
  ) : (
    <>
      <Button
        variant="secondary"
        onClick={() => {
          setDeleteReason('');
          onClose();
        }}
        disabled={isLoading}
      >
        취소
      </Button>
      {requiresApproval ? (
        <Button
          variant="danger"
          onClick={() => {
            if (onRequestDelete && deleteReason.trim() && (!showChannelChoice || sendSms || sendEmail)) {
              // 가용 2개일 때만 선택값 전달. 그 외엔 undefined(서버가 가용 전부 발송).
              const channels: DeleteRequestChannel[] | undefined = showChannelChoice
                ? [...(sendSms ? (['sms'] as const) : []), ...(sendEmail ? (['email'] as const) : [])]
                : undefined;
              onRequestDelete(deleteReason.trim(), channels);
            }
          }}
          disabled={isLoading || !deleteReason.trim() || (showChannelChoice && !sendSms && !sendEmail)}
        >
          {isLoading ? '요청 중...' : '삭제 승인 요청'}
        </Button>
      ) : (
        /* 결재 완료본 삭제는 되돌릴 수 없어 danger, 진행 중은 한 단계 낮은 warning. */
        <Button
          variant={isApproved ? 'danger' : 'warning'}
          onClick={onDelete}
          disabled={isLoading}
        >
          {isLoading ? '삭제 중...' : '삭제'}
        </Button>
      )}
    </>
  );

  // 제목은 아이콘과 나란히 놓이는 본문 헤딩이 소유한다(base 헤더를 쓰지 않는다 — 경고 레벨별
  // 색·아이콘이 제목에 붙어 있다). 접근 이름은 `ariaLabel`로 같은 문자열을 준다.
  // 삭제·요청 진행 중에는 닫기 수단을 전부 막는다 — 종전엔 오버레이 클릭으로 도중에 닫혔다.
  return (
    <Modal
      open
      onClose={onClose}
      ariaLabel={headline}
      size={isSimpleDelete ? 'md' : 'lg'}
      overlayZClassName={MODAL_LAYER.root}
      showCloseButton={false}
      closeOnOverlay={!isLoading}
      closeOnEsc={!isLoading}
      closeLocked={isLoading}
      footer={footer}
    >
        {/* 간단한 삭제 다이얼로그 (초안/반려 상태) */}
        {isSimpleDelete && (
          <div>
            <div className="flex flex-col sm:flex-row sm:items-start">
              <div className="flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-full bg-red-100 mx-auto sm:mx-0">
                <TrashIcon className="h-5 w-5 text-red-600" />
              </div>

              <div className="mt-3 sm:mt-0 sm:ml-4 flex-1 text-center sm:text-left min-w-0">
                <h3 className="text-lg font-medium text-ink-900">{headline}</h3>

                {/* 보고서 정보 */}
                <div className="mt-3 p-3 bg-surface-soft rounded-card">
                  <p className="text-sm font-medium text-ink-900 truncate">{report.title}</p>
                  <div className="mt-1 flex flex-wrap items-center justify-center sm:justify-start gap-2 text-xs text-ink-500">
                    <Badge tone={displayStatusTone} shape="rounded">
                      {displayStatusLabel}
                    </Badge>
                    {report.siteName && <span>{report.siteName}</span>}
                  </div>
                </div>

                <p className="mt-4 text-sm text-ink-600">이 보고서를 삭제하시겠습니까?</p>
              </div>
            </div>
          </div>
        )}

        {/* 보고서 상세 경고 다이얼로그 (결재 진행 중/완료/교육 진행) */}
        {!isSimpleDelete && (
          <div>
            <div className="flex flex-col sm:flex-row sm:items-start">
              {/* 경고 아이콘 */}
              <div className={`flex-shrink-0 flex items-center justify-center h-10 w-10 sm:h-12 sm:w-12 rounded-full mx-auto sm:mx-0 ${
                isApproved ? 'bg-red-200' : 'bg-orange-100'
              }`}>
                <ExclamationIcon className={`h-5 w-5 sm:h-6 sm:w-6 ${
                  isApproved ? 'text-red-700' : 'text-orange-600'
                }`} />
              </div>

              <div className="mt-3 sm:mt-0 sm:ml-4 flex-1 text-center sm:text-left min-w-0">
                <h3 className={`text-lg font-medium ${
                  isApproved ? 'text-red-700' : 'text-orange-700'
                }`}>
                  {headline}
                </h3>

                {/* 보고서 정보 */}
                <div className="mt-3 p-3 bg-surface-soft rounded-card">
                  <p className="text-sm font-medium text-ink-900 truncate">{report.title}</p>
                  <div className="mt-1 flex flex-wrap items-center justify-center sm:justify-start gap-2 text-xs text-ink-500">
                    <Badge tone={displayStatusTone} shape="rounded">
                      {displayStatusLabel}
                    </Badge>
                    {report.siteName && <span>{report.siteName}</span>}
                    <span>{report.authorName}</span>
                  </div>
                </div>

                {/* 경고 메시지 */}
                <div className="mt-4 space-y-3">
                  {/* 승인된 보고서 - 가장 강한 경고 */}
                  {isApproved && (
                    <div className="p-4 bg-red-100 border-2 border-red-400 rounded-card">
                      <div className="flex items-start gap-2">
                        <DangerIcon className="h-6 w-6 text-red-700 flex-shrink-0" />
                        <div className="text-sm text-red-800">
                          <p className="font-bold text-red-900">
                            {isPolicylessCompletion
                              ? '완료된 공식 문서입니다'
                              : '결재가 완료된 공식 문서입니다'}
                          </p>
                          <p className="mt-2">
                            이 보고서는 <strong>{isPolicylessCompletion ? '완료' : '결재가 완료'}</strong>되어 법적 효력을 가진 공식 문서입니다.
                            삭제 시 다음과 같은 심각한 문제가 발생할 수 있습니다:
                          </p>
                          <ul className="mt-2 ml-4 list-disc space-y-1">
                            <li>법적 분쟁 시 증거 자료 소실</li>
                            <li>안전 감사 대응 불가</li>
                            {/* 결재를 거치지 않은 완료본은 지울 서명 기록 자체가 없다. */}
                            {!isPolicylessCompletion && <li>결재자들의 서명 기록 영구 삭제</li>}
                            <li>규정 위반으로 인한 법적 책임</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 교육 진행(결재 전) - 강한 경고. 레거시 TBM 분기의 진행 중 문구와 같다. */}
                  {!isApproved && isEducationOnlyWarning && (
                    <div className="p-4 bg-orange-100 border-2 border-orange-400 rounded-card">
                      <div className="flex items-start gap-2">
                        <DangerIcon className="h-6 w-6 text-orange-700 flex-shrink-0" />
                        <div className="text-sm text-orange-800">
                          <p className="font-bold text-orange-900">교육이 진행 중인 문서입니다</p>
                          <p className="mt-2">
                            참석자 서명이 수집 중입니다.
                            삭제 시 현재까지 수집된 서명과 교육 기록이 모두 소실됩니다.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 결재 진행 중 - 강한 경고 */}
                  {!isApproved && !isEducationOnlyWarning && (
                    <div className="p-4 bg-orange-100 border-2 border-orange-400 rounded-card">
                      <div className="flex items-start gap-2">
                        <DangerIcon className="h-6 w-6 text-orange-700 flex-shrink-0" />
                        <div className="text-sm text-orange-800">
                          <p className="font-bold text-orange-900">결재가 진행 중인 문서입니다</p>
                          <p className="mt-2">
                            일부 결재자가 이미 승인하였습니다.
                            삭제 시 진행된 결재 내역이 모두 소실되며, 결재자들에게 혼란을 줄 수 있습니다.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 복구 불가 경고 */}
                  <div className={`p-3 border rounded-card ${
                    isApproved
                      ? 'bg-red-50 border-red-300'
                      : 'bg-orange-50 border-orange-200'
                  }`}>
                    <div className="flex items-start gap-2">
                      <ExclamationIcon className={`h-5 w-5 flex-shrink-0 mt-0.5 ${
                        isApproved ? 'text-red-600' : 'text-orange-600'
                      }`} />
                      <div className={`text-sm ${isApproved ? 'text-red-800' : 'text-orange-800'}`}>
                        <p className="font-medium">삭제 시 복구할 수 없습니다</p>
                        <p className="mt-1">
                          {isApproved
                            ? isPolicylessCompletion
                              ? '완료 기록과 PDF 파일이 모두 영구 삭제됩니다.'
                              : '승인 기록, 결재자 서명, PDF 파일 모두 영구 삭제됩니다.'
                            : isEducationOnlyWarning
                              ? '수집된 참석자 서명과 교육 기록이 모두 삭제되며, 새 보고서를 생성해야 합니다.'
                              : '진행된 결재 내역이 모두 삭제되며, 새 보고서를 생성해야 합니다.'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 삭제 승인 요청 모드: 결재자 정보 + 사유 입력 */}
                {requiresApproval ? (
                  <div className="mt-4 space-y-3">
                    {/* 결재자 정보 */}
                    {finalApproverName && (
                      <div className="p-3 bg-brand-50 border border-brand-200 rounded-card">
                        <div className="flex items-center gap-2">
                          <svg className="h-4 w-4 text-brand-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          <span className="text-sm text-brand-800">
                            {isApproved ? '최종 결재자' : '현재 최상위 결재자'}: <strong>{finalApproverName}</strong>
                          </span>
                        </div>
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-ink-700 mb-1">
                        삭제 사유 <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        value={deleteReason}
                        onChange={(e) => setDeleteReason(e.target.value)}
                        placeholder={finalApproverName
                          ? `${finalApproverName}님에게 전달할 삭제 사유를 입력하세요`
                          : '결재자에게 전달할 삭제 사유를 입력하세요'}
                        rows={3}
                        className="w-full px-3 py-2 border border-line rounded-control text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      />
                      <p className="mt-2 text-xs text-ink-500">
                        {finalApproverName
                          ? `${finalApproverName}님에게 삭제 승인 링크가 발송됩니다.`
                          : '결재자에게 삭제 승인 링크가 발송됩니다.'}
                      </p>
                    </div>

                    {/* 알림 채널 선택: 가용 2개면 선택, 1개면 안내만 */}
                    {showChannelChoice ? (
                      <div>
                        <label className="block text-sm font-medium text-ink-700 mb-1">
                          전송 방법
                        </label>
                        <div className="flex flex-col gap-2">
                          <label className="flex items-center gap-2 text-sm text-ink-700">
                            <input
                              type="checkbox"
                              checked={sendSms}
                              onChange={(e) => setSendSms(e.target.checked)}
                              className="h-4 w-4 rounded border-ink-300 text-red-600 focus:ring-red-500"
                            />
                            SMS
                          </label>
                          <label className="flex items-center gap-2 text-sm text-ink-700">
                            <input
                              type="checkbox"
                              checked={sendEmail}
                              onChange={(e) => setSendEmail(e.target.checked)}
                              className="h-4 w-4 rounded border-ink-300 text-red-600 focus:ring-red-500"
                            />
                            이메일
                          </label>
                        </div>
                        {!sendSms && !sendEmail && (
                          <p className="mt-1 text-xs text-red-500">
                            최소 한 개의 전송 방법을 선택하세요.
                          </p>
                        )}
                      </div>
                    ) : availableChannels && availableCount === 1 ? (
                      <p className="text-xs text-ink-500">
                        {smsAvailable ? 'SMS' : '이메일'}로 전송됩니다.
                      </p>
                    ) : null}
                  </div>
                ) : (
                  <p className={`mt-4 text-sm font-medium ${
                    isApproved ? 'text-red-700' : 'text-orange-700'
                  }`}>
                    {isApproved
                      ? '위 내용을 모두 확인하였으며, 그래도 이 보고서를 삭제하시겠습니까?'
                      : '위 내용을 확인하였으며 보고서를 삭제하시겠습니까?'}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
    </Modal>
  );
}

// 아이콘 컴포넌트
function TrashIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
      />
    </svg>
  );
}

function ExclamationIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
      />
    </svg>
  );
}

function DangerIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
      />
    </svg>
  );
}

export default ReportDeleteConfirmDialog;
